import type { Approval } from '@/api/types';

/** What each four-eyes action is, in words (design `BO_ACTION`); the key stays visible in mono beside it. */
const ACTION_LABELS: Record<string, string> = {
    'payments.refunds.approve': 'Refund over the limit',
    'identity.merchants.approve': 'Approve a merchant',
    'identity.merchants.suspend': 'Suspend a merchant',
    'admin.flags.kill_switch': 'Flip a kill switch',
    'identity.staff.manage': 'Staff change',
};

/** A staff authenticator reset (BM-34): Identity's approval with the action `identity.staff.manage`, payload kind `totp_reset`. */
export function isStaffTotpReset(
    a: Pick<Approval, 'action' | 'payload'>,
): boolean {
    return (
        a.action === 'identity.staff.manage' && a.payload?.kind === 'totp_reset'
    );
}

export function approvalLabel(a: Pick<Approval, 'action' | 'payload'>): string {
    if (isStaffTotpReset(a)) return 'Reset a staff authenticator';
    if (ACTION_LABELS[a.action]) return ACTION_LABELS[a.action];
    const words = a.action.split('.').slice(-2).join(' ').replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The staff member a reset is for (`staff_public_id`, else the subject), and the requester's reason. */
export function staffResetOf(a: Pick<Approval, 'subject_id' | 'payload'>): {
    staffId: string;
    reason: string | null;
} {
    const p = a.payload ?? {};
    return {
        staffId:
            typeof p.staff_public_id === 'string'
                ? p.staff_public_id
                : a.subject_id,
        reason:
            typeof p.reason === 'string' && p.reason.trim() !== ''
                ? p.reason
                : null,
    };
}

/**
 * Four eyes: the requester never decides their own request, except one its module marked self-approvable (BM-125
 * decision 31: Risk's volume cap); the service enforces it either way.
 */
export function ownLocked(a: Approval, me: string): boolean {
    return (
        a.state === 'pending' &&
        a.requested_by === me &&
        (a.payload as Record<string, unknown> | null)?.self_approvable !== true
    );
}
